<template>
  <div class="salon-app salon-created">
    <!-- SH016 — a bare bar, carrying nothing but the avatar. The screen still has no
         Back: the booking is made, and a back arrow here would invite the customer to
         walk into a flow that is over. The profile menu is a way out, not a way back. -->
    <header class="salon-topbar salon-topbar--bare">
      <div class="salon-topbar-row">
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
    </header>

    <main class="salon-main">
      <!-- The confirmation itself. -->
      <div class="salon-hero">
        <span class="salon-hero-ring" :class="{ 'salon-hero-ring--off': isCancelled }" aria-hidden="true">
          <svg v-if="!isCancelled" viewBox="0 0 24 24" width="32" height="32">
            <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="32" height="32">
            <path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" stroke-width="2.2"
              stroke-linecap="round" />
          </svg>
        </span>
        <h1 class="salon-hero-title">
          {{ isCancelled ? $t("salon.ticket.cancelledTitle") : $t("salon.ticket.booked") }}
        </h1>
        <p class="salon-hero-note">
          {{
            isCancelled
              ? $t("salon.ticket.cancelledNote")
              : $t("salon.ticket.bookedNote")
          }}
        </p>
      </div>

      <!-- The ticket. Perforated across the middle, with a notch punched out of each side. -->
      <div class="salon-ticket" :class="{ 'salon-ticket--off': isCancelled }">
        <div class="salon-ticket-top">
          <div class="salon-ticket-meta">
            <div class="salon-ticket-field">
              <!-- SH013: once POS has taken the order it answers with its own number for
                   the bill, and that is the one the counter works from. -->
              <span class="salon-ticket-label">{{ refLabel }}</span>
              <span class="salon-ticket-value">{{ refValue }}</span>
            </div>
            <div class="salon-ticket-field salon-ticket-field--end">
              <span class="salon-ticket-label">{{ $t("salon.ticket.location") }}</span>
              <span class="salon-ticket-value">{{ booking.branch_name }}</span>
            </div>
          </div>

          <div class="salon-ticket-when">
            <h2 class="salon-ticket-date">{{ dateText }}</h2>
            <p class="salon-ticket-time">{{ timeText }}</p>
            <!-- SH019 — with the bill now filling the half below, the stylist belongs up
                 here beside the date and the time, exactly as the review screen groups
                 the three. Left among the priced lines it read as one of them. -->
            <p class="salon-ticket-stylist">{{ $t("salon.ticket.stylist", { name: stylistText }) }}</p>
          </div>
        </div>

        <div class="salon-ticket-perf" aria-hidden="true"></div>

        <div class="salon-ticket-bottom">
          <div class="salon-ticket-what">
            <!-- SH019 — the ticket itemises what was booked, the way the review screen did.
                 A confirmation that only says "Haircut + Hair Wash" gives the customer
                 nothing to check the counter's bill against. -->
            <div v-if="serviceLines.length" class="salon-ticket-lines">
              <template v-for="line in serviceLines" :key="line.key">
                <div class="salon-ticket-line">
                  <span class="salon-ticket-line-name">{{ line.name }}</span>
                  <span v-if="showPrice" class="salon-ticket-line-price">{{ formatPrice(line.price) }}</span>
                </div>
                <!-- Add-ons read as part of the service above them, not as their own purchase. -->
                <div
                  v-for="addOn in line.add_ons"
                  :key="line.key + '-' + addOn.key"
                  class="salon-ticket-line salon-ticket-line--addon"
                >
                  <span class="salon-ticket-line-name">+ {{ addOn.name }}</span>
                  <span v-if="showPrice" class="salon-ticket-line-price">{{ formatPrice(addOn.price) }}</span>
                </div>
                <p v-if="line.note" class="salon-ticket-line-note">{{ line.note }}</p>
              </template>
            </div>
            <!-- A booking made before SH019 carries no basket on the row, only the summary. -->
            <p v-else class="salon-ticket-service">{{ booking.service_summary }}</p>
          </div>

          <!-- The money, exactly as the API settled it. Nothing here is recomputed on the
               phone: this is the bill, not the preview of one the review screen showed. -->
          <!-- SH032 — an outlet that quotes at the chair does not print a bill here
               either; the counter still has the real one against the booking reference. -->
          <div v-if="showPrice && hasBill" class="salon-ticket-bill">
            <div class="salon-bill-row">
              <span>{{ $t("salon.review.subtotal") }}</span>
              <span>{{ formatPrice(booking.subtotal) }}</span>
            </div>
            <div v-if="discountAmount > 0" class="salon-bill-row salon-bill-row--discount">
              <span>{{ booking.promocode_name || $t("salon.review.voucher") }}</span>
              <span>-{{ formatPrice(discountAmount) }}</span>
            </div>
            <div v-if="showGst" class="salon-bill-row">
              <span>{{ $t("salon.review.gst", { rate: gstRateText }) }}</span>
              <span>{{ formatPrice(booking.gst_amount) }}</span>
            </div>
            <div class="salon-bill-total">
              <span class="salon-bill-total-label">{{ $t("salon.review.total") }}</span>
              <span class="salon-bill-total-value">{{ formatPrice(booking.total_amount) }}</span>
            </div>
          </div>

          <!-- The counter scans the booking reference, so that is what the code carries. -->
          <div v-if="!isCancelled" class="salon-ticket-qr">
            <div class="salon-qr-frame">
              <VueQRCodeComponent
                v-if="booking.booking_code"
                :text="booking.booking_code"
                :size="104"
                error-level="M"
              />
            </div>
            <p class="salon-ticket-qr-note">{{ $t("salon.ticket.qrNote") }}</p>
          </div>
        </div>
      </div>

      <div class="salon-secondary-row">
        <button
          v-if="canCancel"
          class="salon-outline-btn"
          type="button"
          :disabled="isCancelling"
          @click="askToCancel"
        >
          {{ isCancelling ? $t("salon.ticket.cancelling") : $t("salon.ticket.cancelBooking") }}
        </button>
        <button
          v-if="hasDirections"
          class="salon-outline-btn"
          type="button"
          @click="openDirections"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M12 3l9 9-9 9-9-9 9-9z" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linejoin="round" />
            <path d="M10 14v-2.5a1.5 1.5 0 011.5-1.5H15m0 0l-2-2m2 2l-2 2"
              fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ $t("salon.ticket.directions") }}
        </button>
      </div>

      <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>
    </main>

    <nav class="salon-cta">
      <button class="salon-cta-btn" type="button" @click="$emit('my-bookings')">
        {{ $t("salon.ticket.viewBookings") }}
      </button>
      <button class="salon-cta-link" type="button" @click="$emit('book-another')">
        {{ $t("salon.ticket.bookAnother") }}
      </button>
    </nav>

    <!-- Cancelling is not undoable, so it asks first. -->
    <div v-if="showCancelConfirm" class="salon-confirm">
      <div class="salon-confirm-scrim" @click="showCancelConfirm = false"></div>
      <div class="salon-confirm-card" role="dialog" aria-modal="true" :aria-label="$t('salon.ticket.cancelBooking')">
        <h3 class="salon-confirm-title">{{ $t("salon.ticket.confirmTitle") }}</h3>
        <p class="salon-confirm-text">
          {{ $t("salon.ticket.confirmText", { date: dateText, time: timeText, branch: booking.branch_name }) }}
        </p>
        <button class="salon-confirm-danger" type="button" @click="confirmCancel">
          {{ $t("salon.ticket.confirmYes") }}
        </button>
        <button class="salon-confirm-keep" type="button" @click="showCancelConfirm = false">
          {{ $t("salon.ticket.keep") }}
        </button>
      </div>
    </div>

    <material-loading :active="isCancelling" />
  </div>
</template>

<script>
import confetti from "canvas-confetti";
import VueQRCodeComponent from "vue-qrcode-component";
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import { SALON_BOOKING_CANCELLED } from "@/config/const";

// Every three seconds for two minutes, matching MOO's wait for POS on payment success.
const POS_POLL_INTERVAL_MS = 3000;
const POS_POLL_ATTEMPTS = 40;
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH046 — Chinese renders dates as "2026年9月14日 周一". Every other locale keeps the English text.
const ZH_WEEKDAYS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

/**
 * SH010 — the appointment is made (Salon_UI/appointment_created).
 *
 * Everything on screen comes from the row the API just wrote, not from the flow's own
 * state: this is the record of what was booked, and the two could already differ (the API
 * recomputes the money, and a No Preference booking has no stylist yet).
 */
export default {
  name: "SalonAppointmentCreated",
  components: {
    SalonProfileMenu,
    VueQRCodeComponent,
    MaterialLoading,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    booking: {
      type: Object,
      required: true,
    },
    // From SH001. The branch endpoint's own link wins; otherwise the address is handed to
    // Google Maps, which is still better than a dead button.
    destinationLink: {
      type: String,
      default: "",
    },
    // SH032 — minty_branches.salon_show_service_price, carried down from the outlet.
    // False keeps every amount off this screen; the booking still holds them and the
    // counter still bills against the booking reference.
    showPrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["my-bookings", "book-another", "cancelled", "profile"],
  data() {
    return {
      isCancelling: false,
      showCancelConfirm: false,
      errorMessage: "",
      // Mirrors the row once the cancel call succeeds, so the ticket restates itself
      // without the flow having to re-fetch the booking.
      cancelledStatus: 0,
      // SH013 — POS's number for the bill. Empty until POS's polling loop picks the order
      // up, which is normally seconds after the booking and never happens at all if the
      // outlet's POS is off.
      receiptNo: "",
      posTimer: null,
      posAttempts: 0,
      celebrated: false,
    };
  },
  computed: {
    // The ticket carries one reference, and which one depends on whether POS has answered.
    refLabel() {
      return this.receiptNo ? this.$t("salon.ticket.receiptNo") : this.$t("salon.ticket.bookingRef");
    },

    refValue() {
      return this.receiptNo || this.booking.booking_code;
    },

    isCancelled() {
      return (
        this.cancelledStatus === SALON_BOOKING_CANCELLED ||
        Number(this.booking.status) === SALON_BOOKING_CANCELLED
      );
    },

    canCancel() {
      return !this.isCancelled && !!this.booking.can_cancel;
    },

    /**
     * SH019 — the basket as it was written to the booking row.
     *
     * Keyed rather than indexed, for the same reason the review screen keys it: two
     * identical services can sit in one appointment.
     */
    serviceLines() {
      const services = Array.isArray(this.booking.services) ? this.booking.services : [];

      return services.map((service, index) => ({
        key: (service.id ?? "line") + "-" + index,
        name: service.name || "",
        price: Number(service.price || 0),
        note: (service.note || "").trim(),
        add_ons: (service.add_ons || []).map((addOn, addOnIndex) => ({
          key: (addOn.id ?? "addon") + "-" + addOnIndex,
          name: addOn.name || "",
          price: Number(addOn.price || 0),
        })),
      }));
    },

    // A booking written before SH019, or one read back from an older cache, has no money
    // on it at all — showing a $0.00 total there would be a lie, so the block stays away.
    hasBill() {
      return this.booking.total_amount !== undefined && this.booking.total_amount !== null;
    },

    discountAmount() {
      return Number(this.booking.discount_amount || 0);
    },

    // Zero-rated outlets get no GST line rather than a "GST (0%) $0.00" one.
    showGst() {
      return Number(this.booking.gst_rate || 0) > 0;
    },

    // 9, not 9.00 — Number drops the trailing zeros the API's float carries.
    gstRateText() {
      return String(Number(this.booking.gst_rate || 0));
    },

    // "To be assigned" for a No Preference booking — the salon has not picked anybody yet,
    // and saying so is more use than a blank line.
    stylistText() {
      const text = this.booking.staff_text || this.booking.staff_name || "";

      // SH046 — the API writes its own "To be assigned" in English; that one is ours to translate.
      return !text || text === "To be assigned" ? this.$t("salon.ticket.toBeAssigned") : text;
    },

    // SH046 — the API's date_text / time_text are English; Chinese rebuilds them from date / time.
    dateText() {
      const [year, month, day] = String(this.booking.date || "").split("-").map(Number);

      if (this.$i18n.locale !== "zh" || !year || !month || !day) {
        return this.booking.date_text;
      }

      return year + "年" + month + "月" + day + "日 " + ZH_WEEKDAYS[new Date(year, month - 1, day).getDay()];
    },

    timeText() {
      const [hour, minute] = String(this.booking.time || "").split(":").map(Number);

      if (this.$i18n.locale !== "zh" || Number.isNaN(hour) || Number.isNaN(minute) || !this.booking.time) {
        return this.booking.time_text;
      }

      return (hour >= 12 ? "下午" : "上午") + (hour % 12 === 0 ? 12 : hour % 12) + ":" + String(minute).padStart(2, "0");
    },

    mapsUrl() {
      if (this.destinationLink) {
        return this.destinationLink;
      }

      if (this.booking.destination_link) {
        return this.booking.destination_link;
      }

      const query = [this.booking.branch_name, this.booking.branch_address]
        .filter(Boolean)
        .join(" ");

      return query
        ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(query)
        : "";
    },

    hasDirections() {
      return !!this.mapsUrl;
    },
  },
  methods: {
    formatPrice(value) {
      return "$" + Number(value || 0).toFixed(2);
    },

    openDirections() {
      if (!this.mapsUrl) {
        return;
      }

      window.open(this.mapsUrl, "_blank", "noopener");
    },

    askToCancel() {
      this.errorMessage = "";
      this.showCancelConfirm = true;
    },

    async confirmCancel() {
      this.showCancelConfirm = false;

      if (this.isCancelling) {
        return;
      }

      this.isCancelling = true;
      this.errorMessage = "";

      try {
        const response = await SalonBookingService.cancelBooking({
          booking_code: this.booking.booking_code,
        });

        if (response.data.result.isSuccess) {
          this.cancelledStatus = SALON_BOOKING_CANCELLED;
          this.$emit("cancelled", response.data.data.booking);
          return;
        }

        // An appointment that has already started can no longer be called off, and the API
        // says exactly that.
        this.errorMessage =
          response.data.result.message || this.$t("salon.ticket.cancelError");
      } catch (error) {
        this.errorMessage =
          this.$t("salon.ticket.cancelNetworkError");
      } finally {
        this.isCancelling = false;
      }
    },

    /**
     * SH013 — wait for POS to take the order, then celebrate it.
     *
     * The same shape as MOO's payment-success screen: ask every three seconds, because
     * nothing pushes to the phone and POS picks the order up on its own loop. It gives up
     * after two minutes — by then the outlet's POS is off rather than slow, and the customer
     * has their appointment either way, so the screen simply says nothing about it.
     */
    watchForReceipt() {
      if (this.isCancelled || !this.booking.booking_code) {
        return;
      }

      this.posTimer = setInterval(async () => {
        this.posAttempts += 1;

        if (this.posAttempts > POS_POLL_ATTEMPTS) {
          this.stopWatchingForReceipt();
          return;
        }

        try {
          const response = await SalonBookingService.getPosStatus({
            booking_code: this.booking.booking_code,
          });

          const data = response.data.result.isSuccess ? response.data.data : null;

          if (data && data.receipt_no) {
            this.stopWatchingForReceipt();
            this.receiptNo = data.receipt_no;
            this.celebrate();
          }
        } catch (error) {
          // A dropped request is not worth telling the customer about: the next tick asks
          // again, and the booking is made regardless of what POS is doing.
        }
      }, POS_POLL_INTERVAL_MS);
    },

    stopWatchingForReceipt() {
      if (this.posTimer) {
        clearInterval(this.posTimer);
        this.posTimer = null;
      }
    },

    /**
     * The fireworks MOO fires when an order lands on POS — same preset, same one-shot guard.
     */
    celebrate() {
      if (this.celebrated) {
        return;
      }

      this.celebrated = true;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
    },
  },

  mounted() {
    // A ticket opened again later (from My bookings) already carries its receipt number:
    // show it, but do not throw confetti at a booking made last week.
    if (this.booking.receipt_no) {
      this.receiptNo = this.booking.receipt_no;
      this.celebrated = true;
      return;
    }

    this.watchForReceipt();
  },

  beforeUnmount() {
    // The salon flow swaps this screen out without a route change, so nothing else would
    // ever stop the timer.
    this.stopWatchingForReceipt();
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-created {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* SH016 — the bare bar. Same ground as the page behind it, so scrolling the ticket
   under it reads as one surface rather than as a header appearing. */
.salon-topbar--bare {
  position: sticky;
  top: 0;
  z-index: 40;
  padding: 16px var(--salon-container-padding) 0;
  background: var(--salon-background);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 8px var(--salon-container-padding) 200px;
}

/* ── Hero ──────────────────────────────────────────────────────── */
.salon-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  text-align: center;
}

.salon-hero-ring {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 24px;
  color: var(--salon-secondary);
  border: 2px solid var(--salon-secondary);
  border-radius: 999px;
}

.salon-hero-ring--off {
  color: var(--salon-on-surface-variant);
  border-color: var(--salon-surface-container-highest);
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family. Every heading in this file therefore names its own
   face — see the MTH027 PaymentSuccess reskin for the same trap. */
.salon-hero-title {
  margin: 0 0 8px;
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-background);
}

.salon-hero-note {
  max-width: 280px;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Ticket ────────────────────────────────────────────────────── */
.salon-ticket {
  position: relative;
  padding: 24px 20px;
  margin-bottom: var(--salon-stack-lg);
  overflow: hidden;
  background: var(--salon-surface-lowest);
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
}

.salon-ticket--off {
  opacity: 0.6;
}

.salon-ticket-top {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.salon-ticket-meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.salon-ticket-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.salon-ticket-field--end {
  align-items: flex-end;
  text-align: right;
}

.salon-ticket-label {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.08em;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

.salon-ticket-value {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface);
}

.salon-ticket-when {
  margin-top: 8px;
}

.salon-ticket-date {
  margin: 0 0 4px;
  font-family: var(--salon-font-headline);
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-ticket-time {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: var(--salon-on-surface-variant);
}

/* The perforation. The two notches are circles painted in the page's own background and
   pulled past the card's padding, so they read as holes punched through the ticket. The
   card cannot use `overflow: hidden` for them — it does, which is why they are positioned
   to sit exactly on the edge rather than outside it. */
.salon-ticket-perf {
  position: relative;
  height: 2px;
  margin: 24px 0;
  background-image: linear-gradient(
    to right,
    var(--salon-border) 50%,
    transparent 50%
  );
  background-repeat: repeat-x;
  background-size: 8px 100%;
}

.salon-ticket-perf::before,
.salon-ticket-perf::after {
  position: absolute;
  top: 50%;
  z-index: 10;
  width: 24px;
  height: 24px;
  content: "";
  background: var(--salon-background);
  border-radius: 999px;
  transform: translateY(-50%);
}

.salon-ticket-perf::before {
  left: -32px;
}

.salon-ticket-perf::after {
  right: -32px;
}

.salon-ticket-bottom {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.salon-ticket-service {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-ticket-stylist {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── SH019: the itemised bill ──────────────────────────────────── */
.salon-ticket-line {
  display: flex;
  gap: 16px;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.salon-ticket-line-name {
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-ticket-line-price {
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-on-surface);
  /* Tabular figures keep the column of prices aligned on the decimal point. */
  font-variant-numeric: tabular-nums;
}

/* Indented and quieter, so an add-on reads as belonging to the service above it. */
.salon-ticket-line--addon {
  padding-left: 12px;
}

.salon-ticket-line--addon .salon-ticket-line-name,
.salon-ticket-line--addon .salon-ticket-line-price {
  font-size: 14px;
  font-weight: 400;
  color: var(--salon-on-surface-variant);
}

.salon-ticket-line-note {
  margin: -4px 0 8px;
  padding-left: 12px;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
}

.salon-ticket-bill {
  padding-top: 16px;
  margin-top: -8px;
  border-top: 1px solid var(--salon-border);
}

.salon-bill-row {
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
  font-variant-numeric: tabular-nums;
}

.salon-bill-row--discount {
  color: var(--salon-secondary);
}

.salon-bill-total {
  display: flex;
  gap: 16px;
  align-items: baseline;
  justify-content: space-between;
  padding-top: 12px;
  margin-top: 4px;
  border-top: 1px solid var(--salon-border);
}

.salon-bill-total-label {
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-bill-total-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  color: var(--salon-on-surface);
  font-variant-numeric: tabular-nums;
}

.salon-ticket-qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
}

.salon-qr-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 8px;
}

/* The QR component renders a canvas/img of its own into a bare <div>, so the frame has to
   reach through the child it was handed rather than style a class it controls. */
.salon-qr-frame :deep(canvas),
.salon-qr-frame :deep(img),
.salon-qr-frame :deep(table) {
  display: block;
  width: 104px;
  height: 104px;
}

.salon-ticket-qr-note {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

/* ── Secondary actions ─────────────────────────────────────────── */
.salon-secondary-row {
  display: flex;
  gap: 16px;
}

.salon-outline-btn {
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-primary);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.salon-outline-btn:active:not(:disabled) {
  background: var(--salon-surface-variant);
}

.salon-outline-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-error {
  margin: 16px 0 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-error);
  text-align: center;
}

/* ── Bottom actions ────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(20px + env(safe-area-inset-bottom, 0px));
  /* Translucent and blurred, the way the reference draws it. An opaque bar in the page's
     own colour makes the Cancel / Get directions pills passing underneath look sliced off
     rather than scrolled under. */
  background: rgba(251, 249, 246, 0.9);
  backdrop-filter: blur(12px);
  transform: translateX(-50%);
}

.salon-cta-btn {
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.salon-cta-btn:active {
  transform: scale(0.98);
}

.salon-cta-link {
  width: 100%;
  padding: 8px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease;
}

.salon-cta-link:hover {
  color: var(--salon-primary);
}

/* ── Cancel confirmation ───────────────────────────────────────── */
.salon-confirm {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.salon-confirm-scrim {
  position: absolute;
  inset: 0;
  background: rgba(28, 28, 30, 0.45);
}

.salon-confirm-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 24px var(--salon-container-padding)
    calc(24px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface-lowest);
  border-radius: 20px 20px 0 0;
}

.salon-confirm-title {
  margin: 0 0 8px;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-on-surface);
}

.salon-confirm-text {
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-confirm-danger {
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: #ffffff;
  background: var(--salon-error);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.salon-confirm-keep {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>
